import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { savePost } from '../utils/storage';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BoldOutlined, ItalicOutlined, LinkOutlined, PictureOutlined, CodeOutlined } from '@ant-design/icons';
import './NewPost.css';

const DRAFT_KEY = 'my_blog_draft_v1';
const TITLE_MAX = 120;

export default function NewPost() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autosavedAt, setAutosavedAt] = useState(null);
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const fileRef = useRef(null);
  const [active, setActive] = useState({ bold:false, italic:false, heading:false, code:false });
  let autosaveTimer = useRef(null);

  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      try {
        const d = JSON.parse(draft);
        setTitle(d.title || '');
        setContent(d.content || '');
        setAutosavedAt(d.at || null);
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    // debounce autosave
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const payload = { title, content, at: Date.now() };
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
        setAutosavedAt(payload.at);
      } catch (e) {}
    }, 800);
    return () => clearTimeout(autosaveTimer.current);
  }, [title, content]);

  useEffect(() => {
    const ta = contentRef.current;
    if (!ta) return;
    function updateActive(){
      const selStart = ta.selectionStart;
      const selEnd = ta.selectionEnd;
      const text = content || '';
      const before = text.slice(Math.max(0, selStart-3), selStart);
      const after = text.slice(selEnd, Math.min(text.length, selEnd+3));
      setActive({
        bold: /\*\*/.test(before) || /\*\*/.test(after),
        italic: /_/.test(before) || /_/.test(after),
        code: /`/.test(before) || /`/.test(after),
        heading: /^#{1,6}\s/.test(text.slice(0, text.indexOf('\n')===-1? text.length: text.indexOf('\n')))
      });
    }
    ta.addEventListener('select', updateActive);
    ta.addEventListener('keyup', updateActive);
    ta.addEventListener('click', updateActive);
    return ()=>{
      ta.removeEventListener('select', updateActive);
      ta.removeEventListener('keyup', updateActive);
      ta.removeEventListener('click', updateActive);
    };
  }, [content]);

  function clearDraft() {
    localStorage.removeItem(DRAFT_KEY);
    setAutosavedAt(null);
  }

  function onSubmit(e) {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      alert('请填写标题和内容');
      return;
    }
    setSaving(true);
    const id = savePost({ title: title.trim(), content: content.trim() });
    clearDraft();
    navigate(`/post/${id}`);
  }

  function wrapSelection(wrapperLeft, wrapperRight = wrapperLeft){
    const ta = contentRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = content.slice(start, end) || '';
    const left = content.slice(0, start);
    const right = content.slice(end);
    // if already wrapped, unwrap
    if (content.slice(start - wrapperLeft.length, start) === wrapperLeft && content.slice(end, end + wrapperRight.length) === wrapperRight) {
      const newContent = content.slice(0, start - wrapperLeft.length) + sel + content.slice(end + wrapperRight.length);
      setContent(newContent);
      setTimeout(()=>{ ta.focus(); ta.setSelectionRange(start - wrapperLeft.length, start - wrapperLeft.length + sel.length); }, 0);
      return;
    }
    const insert = wrapperLeft + sel + wrapperRight;
    const newContent = left + insert + right;
    setContent(newContent);
    setTimeout(()=>{ ta.focus(); const s = start + wrapperLeft.length; ta.setSelectionRange(s, s + sel.length); },0);
  }

  function toggleHeading(){
    const ta = contentRef.current;
    if (!ta) return;
    const startLine = content.lastIndexOf('\n', ta.selectionStart - 1) + 1;
    const endLine = content.indexOf('\n', ta.selectionStart);
    const lineEnd = endLine === -1 ? content.length : endLine;
    const line = content.slice(startLine, lineEnd);
    if (/^#{1,6}\s/.test(line)){
      // remove leading #s
      const newLine = line.replace(/^#{1,6}\s/, '');
      const newContent = content.slice(0, startLine) + newLine + content.slice(lineEnd);
      setContent(newContent);
      setTimeout(()=>{ ta.focus(); ta.setSelectionRange(startLine, startLine + newLine.length); },0);
    } else {
      const newLine = '### ' + line;
      const newContent = content.slice(0, startLine) + newLine + content.slice(lineEnd);
      setContent(newContent);
      setTimeout(()=>{ ta.focus(); ta.setSelectionRange(startLine + 4, startLine + 4 + (line.length)); },0);
    }
  }

  function insertLink(){
    const ta = contentRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = content.slice(start, end) || '链接文字';
    const url = window.prompt('输入链接（以 http:// 或 https:// 开头）', 'https://');
    if (!url) return;
    const insert = `[${sel}](${url})`;
    const newContent = content.slice(0, start) + insert + content.slice(end);
    setContent(newContent);
    setTimeout(()=>{ ta.focus(); const pos = start + insert.length; ta.setSelectionRange(pos,pos); },0);
  }

  function insertImage(){
    const alt = window.prompt('图片描述（alt，可留空）', '');
    fileRef.current.altHint = alt || '图片';
    fileRef.current?.click();
  }

  function onImagePick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size / 1024 / 1024 > 2) { alert('图片大小不能超过 2MB'); e.target.value=''; return; }
    const reader = new FileReader();
    reader.onload = () => {
      const ta = contentRef.current;
      const start = ta.selectionStart;
      const alt = fileRef.current?.altHint || '图片';
      const insert = `![${alt}](${reader.result})`;
      const newContent = content.slice(0, start) + insert + content.slice(start);
      setContent(newContent);
      setTimeout(()=>{ ta.focus(); ta.setSelectionRange(start + insert.length, start + insert.length); },0);
      fileRef.current.altHint = null;
    };
    reader.readAsDataURL(f);
    e.target.value = '';
  }

  function insertCodeBlock(){
    wrapSelection('```\n','\n```');
  }


  return (
    <div className="container">
      <h1>写新文章</h1>

      <div className="editor-card">
        <form className="editor" onSubmit={onSubmit}>
          <div className="editor-toolbar" role="toolbar" aria-label="编辑工具栏">
            <button type="button" title="加粗 (Ctrl/Cmd+B)" className={`tool-btn ${active.bold? 'active':''}`} onClick={()=>wrapSelection('**','**')}><BoldOutlined /></button>
            <button type="button" title="斜体 (Ctrl/Cmd+I)" className={`tool-btn ${active.italic? 'active':''}`} onClick={()=>wrapSelection('_','_')}><ItalicOutlined /></button>
            <button type="button" title="标题 H3" className={`tool-btn ${active.heading? 'active':''}`} onClick={toggleHeading}>H3</button>
            <button type="button" title="插入链接" className="tool-btn" onClick={insertLink}><LinkOutlined /></button>
            <button type="button" title="插入图片" className="tool-btn" onClick={insertImage}><PictureOutlined /></button>
            <button type="button" title="代码块" className={`tool-btn ${active.code? 'active':''}`} onClick={insertCodeBlock}><CodeOutlined /></button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onImagePick} />

            <div style={{ flex: 1 }} />

            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={preview} onChange={e => setPreview(e.target.checked)} /> 预览
            </label>
          </div>

          <div className="editor-body">
            <div className="editor-left">
              <div className="title-row">
                <input type="text" value={title} maxLength={TITLE_MAX} onChange={e => setTitle(e.target.value)} placeholder="请输入标题" />
                <div className="title-count">{title.length}/{TITLE_MAX}</div>
              </div>

              <textarea ref={contentRef} value={content} onChange={e => setContent(e.target.value)} placeholder="正文（支持 Markdown）" rows={16} onKeyDown={(e)=>{
                const mac = navigator.platform.toLowerCase().includes('mac');
                const ctrl = mac ? e.metaKey : e.ctrlKey;
                if (!ctrl) return;
                if (e.key.toLowerCase() === 'b') { e.preventDefault(); wrapSelection('**','**'); }
                if (e.key.toLowerCase() === 'i') { e.preventDefault(); wrapSelection('_','_'); }
              }} />

              <div className="meta-row">
                <div className="auto-save">{autosavedAt ? `已自动保存 ${(new Date(autosavedAt)).toLocaleTimeString()}` : '尚未保存'}</div>
                <div className="editor-actions">
                  <button type="button" className="btn secondary" onClick={clearDraft}>清除草稿</button>
                  <button className="btn publish" type="submit" disabled={!title.trim() || !content.trim()}>{saving ? '发布中...' : '发布'}</button>
                </div>
              </div>
            </div>

            {preview && (
              <div className="editor-right" aria-live="polite">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || '*预览空白*'}</ReactMarkdown>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
